import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import StatusBadge from "../../components/StatusBadge";
import { PageLoading, ErrorState } from "../../components/States";
import { fmtMoney } from "../../lib/format";
import { useAdminData, act, SearchBox, FilterSelect, Pager, TableCard, TH } from "./shared";

export default function AdminListings() {
  const [sp] = useSearchParams();
  const { data, error, page, setPage, filters, setFilters, reload } = useAdminData(api.adminSpots, { status: sp.get("status") || "" });
  const [q, setQ] = useState("");
  const rows = data?.items || [];

  const review = (s, body, ok) => act(() => api.adminReviewListing(s.spot_id, body), ok, reload);

  return (
    <div data-testid="admin-listings-page">
      <div className="flex flex-wrap gap-3 mb-4">
        <SearchBox value={q} onChange={setQ} onSearch={() => setFilters((f) => ({ ...f, q }))} placeholder="Search title, address or id…" testid="admin-listings-search" />
        <FilterSelect value={filters.status || ""} onChange={(v) => setFilters((f) => ({ ...f, status: v }))} label="State filter" testid="admin-listings-status"
                      options={[["", "All states"], ["active", "Active"], ["pending_review", "Pending review"], ["under_review", "Under review"], ["rejected", "Rejected"], ["suspended", "Suspended"], ["paused", "Paused"]]} />
      </div>
      {error ? <ErrorState body={error} onRetry={reload} /> : !data ? <PageLoading label="Loading listings…" /> : (
        <>
          <TableCard testid="admin-listings-table" empty={rows.length === 0 && "No listings found."}
            head={<><TH>Listing</TH><TH>Host</TH><TH>Price</TH><TH>State</TH><TH right>Actions</TH></>}>
            {rows.map((s) => {
              const state = s.paused ? "occupied" : (s.status || "active");
              const inReview = ["pending_review", "under_review"].includes(s.status);
              return (
                <tr key={s.spot_id} className="border-b border-[#18181B]" data-testid={`admin-spot-${s.spot_id}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      {(s.images || [])[0] && <img src={s.images[0]} alt="" className="w-12 h-9 rounded-lg object-cover border border-[#27272A]" data-testid={`admin-spot-img-${s.spot_id}`} />}
                      <div><p className="font-semibold">{s.title}</p><p className="text-xs text-zinc-500 line-clamp-1">{s.address}</p></div>
                    </div>
                  </td>
                  <td className="p-4 text-zinc-300 text-xs">{s.host_name || s.host_id}</td>
                  <td className="p-4">{fmtMoney(s.price_per_hour)}/hr</td>
                  <td className="p-4"><StatusBadge value={state} /></td>
                  <td className="p-4 text-right space-x-2 whitespace-nowrap">
                    {inReview ? (
                      <>
                        <button data-testid={`approve-${s.spot_id}`} onClick={() => review(s, { action: "approve" }, "Approved — listing is live")}
                                className="text-xs font-semibold px-3 py-1.5 rounded-full border border-emerald-700 text-emerald-400 hover:bg-emerald-950 transition-colors">Approve</button>
                        <button data-testid={`reject-${s.spot_id}`} onClick={() => { const r = window.prompt("Reason for rejection (shown to host):"); if (r !== null) review(s, { action: "reject", reason: r }, "Rejected"); }}
                                className="text-xs font-semibold px-3 py-1.5 rounded-full border border-[#27272A] text-amber-400 hover:border-amber-500 transition-colors">Reject</button>
                      </>
                    ) : (
                      <>
                        {s.status !== "suspended" && s.status !== "rejected" && (
                          <button data-testid={`spot-pause-${s.spot_id}`} onClick={() => act(() => api.adminPatchSpot(s.spot_id, { paused: !s.paused }), "Updated", reload)}
                                  className="text-xs font-semibold px-3 py-1.5 rounded-full border border-[#27272A] hover:border-amber-500 text-amber-400 transition-colors">{s.paused ? "Unpause" : "Pause"}</button>
                        )}
                        <button data-testid={`spot-delete-${s.spot_id}`} onClick={() => window.confirm("Delete this listing permanently?") && act(() => api.adminDeleteSpot(s.spot_id), "Deleted", reload)}
                                className="text-xs font-semibold px-3 py-1.5 rounded-full border border-[#27272A] hover:border-red-500 text-red-400 transition-colors">Delete</button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </TableCard>
          <Pager page={page} total={data.total} pageSize={data.page_size} onPage={setPage} testid="admin-listings-pager" />
        </>
      )}
    </div>
  );
}
